import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { useForgotPassword } from '../hooks/useAuth';

const StyledCard = styled(Card)(() => ({
  maxWidth: '100%',
  width: '95%',
  '@media (min-width: 600px)': {
    width: '80%',
  },
  '@media (min-width: 900px)': {
    width: '70%',
  },
  '@media (min-width: 1200px)': {
    width: '60%',
  },
  '@media (min-width: 1536px)': {
    width: '50%',
  },
  margin: '0 auto',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  borderRadius: 16,
}));

const GradientBackground = styled(Box)(() => ({
  minHeight: '100vh',
  background: `linear-gradient(135deg, 
    #8b9cff15 0%, 
    #ff6b9d10 50%,
    #ffffff 100%)`,
}));

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const forgotPassword = useForgotPassword();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError('');
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      await forgotPassword.mutateAsync(email);
      setSuccess(true);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to send reset email');
    }
  };

  if (success) {
    return (
      <GradientBackground>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <StyledCard>
            {/* Gradient Header Section */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                color: 'white',
                textAlign: 'center',
                py: 3,
                px: 4,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '20px',
                  background: 'linear-gradient(to bottom, rgba(102, 126, 234, 0.1), transparent)',
                }
              }}
            >
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                Check Your Email
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, fontSize: '1.1rem' }}>
                Password reset instructions sent
              </Typography>
            </Box>
            
            {/* Content Section */}
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" color="success.main" gutterBottom>
                ✓ Email Sent!
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                We've sent password reset instructions to <strong>{email}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please check your email and follow the instructions to reset your password.
                The link will expire in 1 hour.
              </Typography>
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7a8bff 0%, #ff5a8d 100%)',
                  },
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                }}
              >
                Back to Login
              </Button>
            </CardContent>
          </StyledCard>
        </Container>
      </GradientBackground>
    );
  }

  return (
    <GradientBackground>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <StyledCard>
          {/* Gradient Header Section */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
              color: 'white',
              textAlign: 'center',
              py: 3,
              px: 4,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '20px',
                background: 'linear-gradient(to bottom, rgba(102, 126, 234, 0.1), transparent)',
              }
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Forgot Password?
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, fontSize: '1.1rem' }}>
              Enter your email to reset your password
            </Typography>
          </Box>
          
          {/* Content Section */}
          <CardContent sx={{ p: 4 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={handleChange}
                required
                placeholder="Enter your email address"
                sx={{ mb: 3 }}
              />
              
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={forgotPassword.isPending}
                sx={{
                  background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7a8bff 0%, #ff5a8d 100%)',
                  },
                  mb: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                }}
              >
                {forgotPassword.isPending ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Email'}
              </Button>
              
              <Box textAlign="center">
                <Link component={RouterLink} to="/login" variant="body1" sx={{ color: '#8b9cff', textDecoration: 'none' }}>
                  Remember your password? Sign in
                </Link>
              </Box>
            </Box>
          </CardContent>
        </StyledCard>
      </Container>
    </GradientBackground>
  );
}

export default ForgotPassword;




